/**
 * Org Detail / Projects tab.
 *
 * Consumes the 32b proxy endpoint GET /api/platform/orgs/:id/projects.
 * The endpoint pass-through to the production server can return 503
 * SERVER_UNAVAILABLE when the upstream is down — surface that as an
 * informative empty state, not a hard error.
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingSkeleton } from '@repro/ui';
import { api } from '../../../utils/api';
import type { ServerProject } from '../../../types/platform';
import type { OrgDetailContext } from '../OrgDetail';

export default function Projects() {
  const { organisation } = useOutletContext<OrgDetailContext>();
  const [projects, setProjects] = useState<ServerProject[] | null>(null);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setServerUnavailable(false);
    api
      .getPlatformOrgProjects(organisation.id)
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setServerUnavailable(true);
          setProjects([]);
        } else {
          setProjects(data);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisation.id]);

  if (loading) {
    return (
      <div className="mt-4">
        <LoadingSkeleton rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <ErrorState title="Couldn't load projects" body={error} />
      </div>
    );
  }

  if (serverUnavailable) {
    return (
      <div className="mt-4">
        <EmptyState
          title="Production server unavailable"
          body="The upstream RePro server didn't respond. Try again in a moment."
        />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState title="No projects" body="This organisation has no projects yet." />
      </div>
    );
  }

  return (
    <div className="mt-4 bg-base-100 shadow rounded-lg overflow-hidden">
      <table className="table w-full">
        <thead>
          <tr>
            <th>Name</th>
            <th className="text-right">Users</th>
            <th className="text-right">Channels</th>
            <th className="text-right">Programs online</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id}>
              <td>
                <div className="font-medium text-base-content">{project.name}</div>
                <code className="text-xs text-base-content/60">{project.guid}</code>
              </td>
              <td className="text-right">{project.user_count ?? '—'}</td>
              <td className="text-right">{project.channel_count ?? '—'}</td>
              <td className="text-right">{project.programs_online_count ?? 0}</td>
              <td className="text-sm text-base-content/70">
                {project.created_at ? formatDate(project.created_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
